import * as React from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { EmailForm } from "./EmailForm";
import { NewUserForm } from "./NewUserForm";
import { ExistingUserForm } from "./ExistingUserForm";
import { CodeForm } from "./CodeForm";
import { AuthResultDto } from "@/lib/auth-api";
import { authPromise } from "@/lib/auth";

type AuthTab =
  | { tab: "email" }
  | { tab: "newUser"; email: string; useCaptcha: boolean }
  | { tab: "existingUser"; email: string }
  | { tab: "code"; email: string; password: string };

interface SignInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const SignInModal: React.FC<SignInModalProps> = ({ open, onOpenChange, onSuccess }) => {
  const [activeTab, setActiveTab] = React.useState<AuthTab>({ tab: "email" });

  React.useEffect(() => {
    if (open) {
      setActiveTab({ tab: "email" });
    }
  }, [open]);

  const handleLoginSuccess = async (result: AuthResultDto) => {
    const auth = await authPromise;
    if (auth) {
      await auth.loginWithToken(result.auth_token);
    }
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl">
        <VisuallyHidden>
          <DialogTitle>Sign In</DialogTitle>
        </VisuallyHidden>

        {activeTab.tab === "email" ? (
          <EmailForm
            onEmailVerified={(email, userExists, useCaptcha) => {
              setActiveTab(
                userExists ? { tab: "existingUser", email } : { tab: "newUser", email, useCaptcha },
              );
            }}
            onGoogleLogin={() => {}}
          />
        ) : activeTab.tab === "newUser" ? (
          <NewUserForm
            email={activeTab.email}
            showCaptcha={activeTab.useCaptcha}
            onBack={() => setActiveTab({ tab: "email" })}
            onSuccess={handleLoginSuccess}
          />
        ) : activeTab.tab === "existingUser" ? (
          <ExistingUserForm
            email={activeTab.email}
            onBack={() => setActiveTab({ tab: "email" })}
            onTwoFactor={(password) => setActiveTab({ tab: "code", email: activeTab.email, password })}
          />
        ) : activeTab.tab === "code" ? (
          <CodeForm
            email={activeTab.email}
            password={activeTab.password}
            onBack={() => setActiveTab({ tab: "existingUser", email: activeTab.email })}
            onSuccess={handleLoginSuccess}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
